export function cn(...classes) {
  return classes.filter(Boolean).join(" ");
}

export function ensureArray(value) {
  return Array.isArray(value) ? value : [];
}

export function stripHtml(html = "") {
  return String(html)
    .replace(/<[^>]+>/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

export function truncateText(value = "", maxLength = 220) {
  const text = String(value || "").trim();

  if (text.length <= maxLength) return text;

  return `${text.slice(0, maxLength - 1).replace(/\s+\S*$/, "")}…`;
}

export function normalizeSearchText(value = "") {
  return String(value || "")
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .toLowerCase()
    .trim();
}

export function toBoolean(value) {
  return value === true || value === 1 || value === "1";
}

export function normalizeExternalUrl(value = "") {
  const url = String(value || "").trim();
  if (!url) return "";

  if (/^https?:\/\//i.test(url)) return url;

  return `https://${url}`;
}

export function normalizePhoneHref(value = "") {
  const phone = String(value || "")
    .replace(/\s+/g, "")
    .replace(/[().-]/g, "");

  return phone ? `tel:${phone}` : "";
}

export function parseEmails(value = "") {
  return String(value || "")
    .split(/[;,]/)
    .map((mail) => mail.trim())
    .filter(Boolean);
}

export function buildAddress(service) {
  const address = service?.adresse_service || "";
  const cpVille = [service?.cp_service, service?.ville_service]
    .filter(Boolean)
    .join(" ");

  return [address, cpVille].filter(Boolean).join("\n");
}

export function buildAddressInline(service) {
  const address = service?.adresse_service || "";
  const cpVille = [service?.cp_service, service?.ville_service]
    .filter(Boolean)
    .join(" ");

  return [address, cpVille].filter(Boolean).join(" ");
}

export function buildMapsHref({ gps, address }) {
  if (gps) {
    return `https://www.google.com/maps?q=${encodeURIComponent(gps)}`;
  }

  if (address) {
    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
      address
    )}`;
  }

  return "";
}

export function buildMapEmbedUrl({ gps, address }) {
  const query = gps || address;

  if (!query) return "";

  return `https://maps.google.com/maps?q=${encodeURIComponent(
    query
  )}&z=16&output=embed`;
}

export function normalizeServiceMunicipal(service) {
  const title = service?.nom_service || "Service municipal";
  const category = service?.categorie_service || "";
  const type = service?.type_service || "";
  const accroche = service?.accroche_service || "";

  const descriptionRaw = service?.description_service || "";
  const descriptionText = stripHtml(descriptionRaw);
  const descriptionPreview = truncateText(descriptionText, 240);

  const emails = parseEmails(service?.mail_service);
  const address = buildAddress(service);
  const addressInline = buildAddressInline(service);

  const mapsHref = buildMapsHref({
    gps: service?.gps_service,
    address: addressInline,
  });

  const mapEmbedUrl = buildMapEmbedUrl({
    gps: service?.gps_service,
    address: addressInline,
  });

  const websiteUrl = normalizeExternalUrl(service?.website_service);
  const otherUrl = normalizeExternalUrl(service?.autre_url_service);

  const normalized = {
    id: service?.id,
    raw: service,

    title,
    category,
    type,
    accroche,

    descriptionRaw,
    descriptionText,
    descriptionPreview,

    phone: service?.tel_service || "",
    phoneHref: normalizePhoneHref(service?.tel_service),

    phoneSecondary: service?.tel_secondaire || "",
    phoneSecondaryHref: normalizePhoneHref(service?.tel_secondaire),

    emails,

    websiteUrl,
    otherUrl,

    address,
    addressInline,
    gps: service?.gps_service || "",
    mapsHref,
    mapEmbedUrl,
    hasMap: Boolean(mapsHref || mapEmbedUrl),

    horaires: service?.horaires_service || "",
    infos: service?.infos_complementaires || "",

    ordre: Number(service?.ordre_affichage ?? service?.display_order ?? 9999),
  };

  normalized.searchIndex = normalizeSearchText(
    [
      normalized.title,
      normalized.category,
      normalized.type,
      normalized.accroche,
      normalized.descriptionText,
      normalized.phone,
      normalized.phoneSecondary,
      normalized.emails.join(" "),
      normalized.websiteUrl,
      normalized.otherUrl,
      normalized.address,
      normalized.horaires,
      normalized.infos,
    ]
      .filter(Boolean)
      .join(" ")
  );

  return normalized;
}

export function sortServices(items, sort = "nom") {
  return [...items].sort((a, b) => {
    if (a.ordre !== b.ordre) {
      return a.ordre - b.ordre;
    }

    if (sort === "categorie") {
      return normalizeSearchText(a.category).localeCompare(
        normalizeSearchText(b.category),
        "fr"
      );
    }

    if (sort === "type") {
      return normalizeSearchText(a.type).localeCompare(
        normalizeSearchText(b.type),
        "fr"
      );
    }

    return normalizeSearchText(a.title).localeCompare(
      normalizeSearchText(b.title),
      "fr"
    );
  });
}